import React, { useEffect, useMemo, useState } from "react";
import ReferralTrackingRow from "./ReferralTrackingRow";
import "../../../../../../styles/ambassadorChallenge/referralTracking.scss";
import axios from "axios";
import { isAuth } from "../../../../../../pages/auth/ProtectedRoute";
import TableRowSkeleton from "./TableRowSkeleton";


const PAGE_SIZE = 10;

const ReferralTracking: React.FC = () => {
  const [data, setData] = useState<ReferralTracking[]>([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReferralTracking = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_HOST_API}ambassador/referral-tracking`,
          {
            headers: {
              Authorization: `Bearer ${isAuth()}`,
            },
          }
        );
        setData(res.data.data);
      } catch (err: any) {
        console.log("error fetching referral tracking data:", data);
      } finally {
        setLoading(false);
      }
    };

    fetchReferralTracking();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((d) => {
      return (
        d.referrer.name.toLowerCase().includes(q) ||
        d.referred.name.toLowerCase().includes(q)
      );
    });
  }, [data, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setPage(1);
  };

  return (
    <section className="rt-card">
      <header className="rt-header">
        <div className="title-block">
          <h3>Referral Tracking</h3>
          <p className="subtitle">
            Monitor referral validation and engagement completion
          </p>
        </div>

        <div className="search-area">
          <svg
            className="icon-search"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle cx="11" cy="11" r="6" stroke="#bbb" strokeWidth="1.5" />
            <path
              d="M21 21l-4.35-4.35"
              stroke="#bbb"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <input
            placeholder="Search by referrer or referred username"
            value={query}
            onChange={onSearch}
          />
        </div>
      </header>

      <div className="criteria-box">
        <p>
          Engagement Criteria{" "}
          <span className="muted">(all must be completed)</span>
        </p>

        <div className="criteria-list">
          <div className="crit">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_11208_69711)">
                <path
                  d="M5 12L10 17L20 7"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_11208_69711">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div>Connect with at least one user</div>
          </div>

          <div className="crit">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_11208_69711)">
                <path
                  d="M5 12L10 17L20 7"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_11208_69711">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div>Update profile to at least 80%</div>
          </div>

          <div className="crit">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_11208_69711)">
                <path
                  d="M5 12L10 17L20 7"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_11208_69711">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div>Create at least one post</div>
          </div>
        </div>
      </div>

      <div className="rt-table">
        <div className="rt-table-head">
          <div className="col id">Referrer ID</div>
          <div className="col referrer">Referrer</div>
          <div className="col referred">Referred</div>
          <div className="col date">Signup date</div>
          <div className="col progress">Engagement Progress</div>
          <div className="col status">Status</div>
        </div>

        <div className="rt-table-body">
          {/* {pageData && pageData.map((item) => (
            <ReferralTrackingRow key={item.referrer_id} item={item} />
          ))} */}

          {loading
            ? Array.from({ length: PAGE_SIZE }).map((_, idx) => (
                <TableRowSkeleton key={idx} />
              ))
            : pageData &&
              pageData.map((item) => (
                <ReferralTrackingRow key={item.referrer_id} item={item} />
              ))}
        </div>

        <footer className="rt-footer">
          <div className="page-info">
            Page {page} of {pageCount}
          </div>

          <div className="pagination">
            <button
              className="page-btn"
              disabled={page <= 1}
              onClick={() => setPage((s) => Math.max(1, s - 1))}
            >
              Previous
            </button>

            <button
              className="page-btn"
              disabled={page >= pageCount}
              onClick={() => setPage((s) => Math.min(pageCount, s + 1))}
            >
              Next
            </button>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default ReferralTracking;
